import { QueryClient } from "@tanstack/react-query";
import { NavigateFunction } from "react-router-dom";

import { FileIndexNeeded } from "../../../PageView/PageViewBrieGPT/BrieGPTUploadButton";
import { FileInterface } from "../../../../types/RecordInterfaces";
import { getErrorMessage } from "../../../../utils/GeneralUtils";
import { updateToastEvent } from "../../../../utils/ToastUtil";
import { AppConfirm } from "../../../../utils/PopupUtil";
import TenantAPI from "../../../../api/api/TenantAPI";
import { SUPPORTED_EXTENSION_VALUES } from "../../../../utils/Constants";

const FILE_SIZE_LIMIT = 20 * 1024 * 1024; // 20 MB in bytes

export function canBeIndexed(file: FileInterface) {
  if (!file.Size || file.Size > FILE_SIZE_LIMIT) return false;

  if (!SUPPORTED_EXTENSION_VALUES.includes(file.Type!)) return false;

  return true;
}

export async function IndexFiles(
  filesToIndex: { file: FileInterface; fieldAPIName: string }[],
  recordID: string,
  moduleAPIName: string,
  navigate: NavigateFunction,
  tenantID: string,
  client: QueryClient
) {
  const indexResults = await Promise.allSettled(
    filesToIndex?.map((a) =>
      TenantAPI.indexFile({
        fileRecordID: a.file.RecordID!,
        recordID: recordID,
        fieldAPIName: a.fieldAPIName,
        moduleAPIName: moduleAPIName,
      })
    )
  );

  let successfulIndexCount = 0;
  let failedIndexCount = 0;

  for (const indexResult of indexResults) {
    if (indexResult.status === "rejected") {
      const errorMessage = getErrorMessage(indexResult.reason);

      if (errorMessage === "File index is not built.") {
        updateToastEvent("success", "Indexing failed.", "indexing", {
          autoClose: true,
          loading: false,
        });
        return FileIndexNeeded(navigate, tenantID!);
      }

      failedIndexCount++;
      continue;
    } else {
      successfulIndexCount++;
    }
  }

  client.invalidateQueries({
    queryKey: [tenantID, "record", moduleAPIName, recordID],
  });

  updateToastEvent("success", "Indexing complete", "indexing", {
    autoClose: true,
    loading: false,
  });

  await AppConfirm({
    title: "Indexing Complete",
    text: `${successfulIndexCount} files were successfully indexed. ${
      failedIndexCount > 0 ? `${failedIndexCount} files failed to index.` : ""
    }`,
    confirmButtonText: "OK",
    confirmButtonColor: "var(--primary)",
  });
}
